/**
 * <ReviewPhase> — second half of AutoReconcileDialog: shows the three
 * buckets from /api/reconcile/match and lets the user act on each:
 *
 *   1. Matched          — pre-ticked, untick anything wrong.
 *   2. Unmatched stmt   — statement rows the matcher couldn't pair.
 *                         Each row has a "+ Add" toggle (import as new
 *                         transaction) AND a pair-tag input (type "1"
 *                         here and "1" on an app txn below to override
 *                         the app txn's amount).
 *   3. Unmatched app    — Forge transactions that didn't appear on the
 *                         statement. Each row has a pair-tag input
 *                         (use to pair up with an unmatched stmt row).
 */
import React from 'react';
import { CheckCircle2, Loader2, Plus, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { fmtMoney } from '../../lib/format';
import Stat from './Stat';
import { stmtKey } from './useAutoReconcile';

const ReviewPhase = ({
  result,
  selectedIds,
  importIds,
  pairTags,
  applying,
  onToggleMatch,
  onToggleImport,
  onPairTag,
  onApply,
  onReset,
}) => {
  const matches = result?.matched || [];
  const unmatchedStmt = result?.unmatched_statement || [];
  const unmatchedApp = result?.unmatched_app || [];

  // Count how many pair tags exist on each side, for the apply-button label.
  const stmtTags = Object.keys(pairTags).filter((k) => k.startsWith('stmt::'));
  const txnTags = Object.keys(pairTags).filter((k) => k.startsWith('txn::'));
  const pairedTagValues = new Set(
    stmtTags.map((k) => pairTags[k]).filter((t) => txnTags.some((tk) => pairTags[tk] === t)),
  );
  const completePairs = pairedTagValues.size;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-2 text-center">
        <Stat label="Stmt rows" value={result.summary.statement_rows} />
        <Stat label="Matched" value={result.summary.matched_count} accent="emerald" />
        <Stat label="No match" value={result.summary.unmatched_statement_count} accent="amber" />
        <Stat label="App-only" value={result.summary.unmatched_app_count} accent="slate" />
      </div>

      {/* SECTION A — auto-matched ----------------------------------------- */}
      {matches.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-[#020617] mb-2">
            Confident matches <span className="text-[#64748B] font-normal">— untick anything wrong</span>
          </h3>
          <div className="border border-[#E2E8F0] rounded-lg overflow-hidden max-h-[260px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-[#64748B]">Statement</th>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-[#64748B]">Forge Ledger</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-[#64748B]">Score</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((m) => (
                  <MatchRow
                    key={m.transaction.transaction_id}
                    match={m}
                    checked={selectedIds.has(m.transaction.transaction_id)}
                    onToggle={() => onToggleMatch(m.transaction.transaction_id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* SECTION B — unmatched statement rows ----------------------------- */}
      {unmatchedStmt.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-[#020617] mb-1">
            Statement rows with no match{' '}
            <span className="text-[#64748B] font-normal">
              — add as new transactions, or tag with a number to pair below
            </span>
          </h3>
          <div className="border border-[#E2E8F0] rounded-lg overflow-hidden max-h-[220px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-[#64748B]">Statement row</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-[#64748B]">Amount</th>
                  <th className="px-3 py-2 text-center text-xs uppercase tracking-wider text-[#64748B] w-16">Pair</th>
                  <th className="px-3 py-2 text-center text-xs uppercase tracking-wider text-[#64748B] w-24">Action</th>
                </tr>
              </thead>
              <tbody>
                {unmatchedStmt.map((row) => {
                  const key = stmtKey(row);
                  const isImporting = importIds.has(key);
                  const tag = pairTags[`stmt::${key}`] || '';
                  return (
                    <tr key={key} className="border-t border-[#E2E8F0]" data-testid={`unmatched-stmt-${key}`}>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-[#020617]">{row.description || '—'}</div>
                        <div className="text-xs text-[#64748B]">{row.date}{row.reason ? ` · ${row.reason}` : ''}</div>
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <span className={row.amount < 0 ? 'text-[#DC2626]' : 'text-[#10B981]'}>
                          {row.amount < 0 ? '−' : '+'}£{fmtMoney(Math.abs(row.amount))}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={tag}
                          onChange={(e) => onPairTag('stmt', key, e.target.value)}
                          className="w-10 h-7 text-center text-sm border border-[#CBD5E1] rounded focus:border-[#0052FF] focus:outline-none"
                          placeholder="–"
                          aria-label="Pair tag"
                          data-testid={`stmt-pair-tag-${key}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        <button
                          type="button"
                          onClick={() => onToggleImport(key)}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                            isImporting
                              ? 'bg-[#10B981] text-white'
                              : 'border border-[#CBD5E1] text-[#475569] hover:border-[#0052FF] hover:text-[#0052FF]'
                          }`}
                          data-testid={`stmt-import-${key}`}
                        >
                          {isImporting ? <><Check className="w-3 h-3" /> Added</> : <><Plus className="w-3 h-3" /> Add</>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* SECTION C — app-only transactions -------------------------------- */}
      {unmatchedApp.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-[#020617] mb-1">
            Forge transactions not on the statement{' '}
            <span className="text-[#64748B] font-normal">
              — tag with a matching number above to pair (overrides the amount)
            </span>
          </h3>
          <div className="border border-[#E2E8F0] rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-[#64748B]">Forge transaction</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-[#64748B]">Amount</th>
                  <th className="px-3 py-2 text-center text-xs uppercase tracking-wider text-[#64748B] w-16">Pair</th>
                </tr>
              </thead>
              <tbody>
                {unmatchedApp.map((t) => {
                  const tag = pairTags[`txn::${t.transaction_id}`] || '';
                  return (
                    <tr key={t.transaction_id} className="border-t border-[#E2E8F0]" data-testid={`unmatched-app-${t.transaction_id}`}>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-[#020617]">{t.description}</div>
                        <div className="text-xs text-[#64748B]">{t.date} · {t.transaction_type}</div>
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <span className={t.transaction_type === 'expense' ? 'text-[#DC2626]' : 'text-[#10B981]'}>
                          {t.transaction_type === 'expense' ? '−' : '+'}£{fmtMoney(Math.abs(t.amount))}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={tag}
                          onChange={(e) => onPairTag('txn', t.transaction_id, e.target.value)}
                          className="w-10 h-7 text-center text-sm border border-[#CBD5E1] rounded focus:border-[#0052FF] focus:outline-none"
                          placeholder="–"
                          aria-label="Pair tag"
                          data-testid={`txn-pair-tag-${t.transaction_id}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {matches.length === 0 && unmatchedStmt.length === 0 && unmatchedApp.length === 0 && (
        <div className="text-center text-sm text-[#64748B] py-6 border border-dashed border-[#E2E8F0] rounded-lg">
          No matches and no leftover rows. Either you already reconciled this period, or the file headers weren&apos;t recognised.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E2E8F0]">
        <div className="text-xs text-[#64748B] mr-auto">
          {selectedIds.size} match{selectedIds.size === 1 ? '' : 'es'} ticked
          {completePairs > 0 ? ` · ${completePairs} pair${completePairs === 1 ? '' : 's'}` : ''}
          {importIds.size > 0 ? ` · ${importIds.size} to import` : ''}
        </div>
        <Button type="button" onClick={onReset} className="bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]" data-testid="auto-reconcile-start-over">
          Start over
        </Button>
        <Button
          type="button"
          onClick={onApply}
          disabled={applying}
          className="bg-[#10B981] text-white hover:bg-[#059669]"
          data-testid="auto-reconcile-apply"
        >
          {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Apply
        </Button>
      </div>
    </div>
  );
};

const MatchRow = ({ match, checked, onToggle }) => (
  <tr
    className="border-t border-[#E2E8F0]"
    data-testid={`auto-reconcile-match-row-${match.transaction.transaction_id}`}
  >
    <td className="px-3 py-2 align-top">
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        aria-label="Apply this match"
        data-testid={`auto-reconcile-match-checkbox-${match.transaction.transaction_id}`}
      />
    </td>
    <td className="px-3 py-2 align-top">
      <div className="font-medium text-[#020617]">{match.statement.description || '—'}</div>
      <div className="text-xs text-[#64748B]">
        {match.statement.date} · £{fmtMoney(Math.abs(match.statement.amount))}
      </div>
    </td>
    <td className="px-3 py-2 align-top">
      <div className="font-medium text-[#020617]">{match.transaction.description}</div>
      <div className="text-xs text-[#64748B]">
        {match.transaction.date} · £{fmtMoney(match.transaction.amount)} · {match.transaction.transaction_type}
      </div>
    </td>
    <td className="px-3 py-2 align-top text-right text-xs font-semibold text-[#0052FF]">
      {match.score.toFixed(0)}
    </td>
  </tr>
);

export default ReviewPhase;
